// Confirmation for actions that can't be undone (leave company, delete account; WEB_PLAN.md §4.7): the person types
// a word or the company's name before the red button turns on, so a stray click can't delete anything. Errors from
// the action show inside the dialog, which stays open so the person can read them and cancel.

import { clsx } from 'clsx';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ErrorText, SecondaryButton, TextInput } from '@/features/auth/components/ui';

/** Case- and space-insensitive comparison of what was typed with what is asked for. */
export function matchesConfirmation(typed: string, expected: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();
  return norm(expected) !== '' && norm(typed) === norm(expected);
}

export function TypedConfirmDialog({
  title,
  message,
  confirmWord,
  confirmLabel,
  busy,
  busyLabel,
  error,
  errorTitle,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: ReactNode;
  /** What has to be typed ("DELETE", the company name). */
  confirmWord: string;
  confirmLabel: string;
  busy: boolean;
  busyLabel: string;
  error: string | null;
  /** Heading above `error` ("Couldn't leave Acme"). */
  errorTitle?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const titleId = useId();
  const input = useRef<HTMLInputElement | null>(null);
  const confirmed = matchesConfirmation(typed, confirmWord);

  useEffect(() => {
    input.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--gt-navy-deep)]/40 p-4 backdrop-blur-[2px] sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-900"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--gt-red)]/12 text-[var(--gt-red)]">
            <TriangleAlert className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-bold text-[var(--gt-navy)] dark:text-slate-50">
              {title}
            </h2>
            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{message}</div>
          </div>
        </div>
        <form
          noValidate
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (confirmed && !busy) onConfirm();
          }}
        >
          <label htmlFor={inputId} className="text-sm text-slate-700 dark:text-slate-300">
            Type <span className="font-semibold text-slate-900 select-all dark:text-white">{confirmWord}</span> to confirm.
          </label>
          <TextInput
            id={inputId}
            ref={input}
            value={typed}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            disabled={busy}
            onChange={(e) => setTyped(e.target.value)}
          />
          {error && (
            <div className="flex flex-col gap-1">
              {errorTitle && <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{errorTitle}</p>}
              <ErrorText>{error}</ErrorText>
            </div>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <SecondaryButton onClick={onCancel} disabled={busy}>
              Cancel
            </SecondaryButton>
            <button
              type="submit"
              disabled={!confirmed || busy}
              aria-busy={busy || undefined}
              className={clsx(
                'flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--gt-red)] px-4 py-3 text-sm font-bold text-white transition',
                'hover:brightness-105 focus-visible:ring-2 focus-visible:ring-[var(--gt-red)]/40 focus-visible:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            >
              {busy ? busyLabel : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
